import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, RefreshCw, FileSpreadsheet, Hash, IndianRupee, User, Layers } from 'lucide-react';
import * as XLSX from 'xlsx';
import { vouchersApi } from '../services/api';

// Who is chasing what. One row per person who logged the latest followup on an
// outstanding bill — or, in group scope, per ledger group, opening from Sundry
// Debtors down through its sub-groups. One column per bucket, and a bill sits
// in exactly one of them, so the columns add up to the bill count.

const fmt = (n: any) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const UNFOLLOWED = 'Not followed up';
const UNGROUPED = 'Ungrouped';
const ROOT_GROUP = 'Sundry Debtors';

type Buckets = {
  bills: number; amount: number;
  followup: number; no_reply: number; payment: number; error: number; frustitting: number;
  today: number; overdue: number; no_next_date: number;
  followup_amount: number; no_reply_amount: number; payment_amount: number; error_amount: number;
  frustitting_amount: number; today_amount: number; overdue_amount: number; no_next_date_amount: number;
};
type Row = Buckets & { label: string; group_id?: number | null; parent_id?: number | null };
// Every bucket carries both figures; this switch picks which one the cells show.
type Mode = 'count' | 'amount';
// Whose followups a row stands for: the person chasing, or the party's group.
type Scope = 'user' | 'group';

// A group and everything filed under it. `own` is the group's own parties;
// `roll` adds its sub-groups, which is what the collapsed row shows.
type Node = { id: number | null; name: string; own: Buckets; roll: Buckets; children: Node[]; depth: number };

const BUCKET_KEYS: (keyof Buckets)[] = ['followup', 'no_reply', 'payment', 'error', 'frustitting', 'today', 'overdue', 'no_next_date'];
const ZERO = (): Buckets => ({
  bills: 0, amount: 0,
  followup: 0, no_reply: 0, payment: 0, error: 0, frustitting: 0,
  today: 0, overdue: 0, no_next_date: 0,
  followup_amount: 0, no_reply_amount: 0, payment_amount: 0, error_amount: 0,
  frustitting_amount: 0, today_amount: 0, overdue_amount: 0, no_next_date_amount: 0,
});
const addInto = (t: any, r: any) => {
  t.bills += Number(r.bills || 0); t.amount += Number(r.amount || 0);
  for (const k of BUCKET_KEYS) { t[k] += Number(r[k] || 0); t[`${k}_amount`] += Number(r[`${k}_amount`] || 0); }
  return t;
};

// Same colours as the Outstanding report's pills. `bucket` is what the drill
// passes so the bill list shows exactly the rows behind the number.
const COLUMNS: { key: keyof Buckets; amountKey: keyof Buckets; label: string; color: string; bucket: string }[] = [
  { key: 'bills',        amountKey: 'amount',              label: 'All',              color: '#16a34a', bucket: 'all' },
  { key: 'followup',     amountKey: 'followup_amount',     label: 'Followup',         color: '#007bff', bucket: 'followup' },
  { key: 'no_reply',     amountKey: 'no_reply_amount',     label: 'No Reply',         color: '#f59e0b', bucket: 'no_reply' },
  { key: 'payment',      amountKey: 'payment_amount',      label: 'Payment',          color: '#28a745', bucket: 'payment' },
  { key: 'error',        amountKey: 'error_amount',        label: 'Error',            color: '#dc3545', bucket: 'error' },
  { key: 'frustitting',  amountKey: 'frustitting_amount',  label: 'Frustitting',      color: '#fd7e14', bucket: 'frustitting' },
  { key: 'today',        amountKey: 'today_amount',        label: "Today's Followup", color: '#6610f2', bucket: 'today' },
  { key: 'overdue',      amountKey: 'overdue_amount',      label: 'Followup Overdue', color: '#c0392b', bucket: 'overdue' },
  { key: 'no_next_date', amountKey: 'no_next_date_amount', label: 'Pending Followup', color: '#64748b', bucket: 'no_next_date' },
];

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// What a visible line in the table is: a group (collapsed = roll-up), that
// group's own parties when it also has sub-groups, or a person.
type Line = { key: string; label: string; depth: number; figures: Buckets; nodeId?: number | null; expandable?: boolean; expanded?: boolean; direct?: boolean };

const FollowupUserReport: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // The route decides the scope, so each report keeps its own menu entry and
  // its own link; the switch below just moves between the two.
  const scope: Scope = pathname.includes('followup-groups') ? 'group' : 'user';
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [side, setSide] = useState<'receivable' | 'payable' | 'all'>('receivable');
  const [dateTo, setDateTo] = useState(todayStr());
  const [totalBills, setTotalBills] = useState(0);
  const [mode, setMode] = useState<Mode>('count');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = scope === 'group'
        ? await vouchersApi.getFollowupGroupSummary({ side, date_to: dateTo })
        : await vouchersApi.getFollowupUserSummary({ side, date_to: dateTo });
      const raw = (res as any)?.data?.rows || [];
      setRows(raw.map((r: any) => ({ ...r, label: scope === 'group' ? r.group : r.user })));
      setTotalBills((res as any)?.data?.total_bills || 0);
    } catch (e: any) {
      setError(e?.message || 'Could not load the report');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [scope, side, dateTo]);
  useEffect(() => { load(); }, [load]);

  // ── The group tree, biggest first, with each node's roll-up ──────────────
  const tree = useMemo<Node[]>(() => {
    if (scope !== 'group') return [];
    const nodes = new Map<number, Node>();
    const loose: Node[] = [];
    for (const r of rows) {
      const node: Node = { id: r.group_id ?? null, name: r.label, own: addInto(ZERO(), r), roll: ZERO(), children: [], depth: 0 };
      if (r.group_id != null) nodes.set(Number(r.group_id), node); else loose.push(node);
    }
    // The report is about Sundry Debtors, so that is the top line — not the
    // Current Assets / Current Liabilities wrappers above it, which say
    // nothing here. Its sub-groups keep their real shape underneath; a group
    // filed elsewhere (Sundry Creditors and the like) becomes its own top
    // line, nested under the nearest outside group that carries bills.
    const parentIdOf = new Map<number, number | null>();
    for (const r of rows) if (r.group_id != null) parentIdOf.set(Number(r.group_id), r.parent_id != null ? Number(r.parent_id) : null);
    const ancestors = (id: number): number[] => {
      const out: number[] = [];
      let cur = parentIdOf.get(id) ?? null;
      for (let hops = 0; cur != null && hops < 50; hops++) { out.push(cur); cur = parentIdOf.get(cur) ?? null; }
      return out;
    };
    const rootId = rows.find(r => r.label === ROOT_GROUP && r.group_id != null)?.group_id ?? null;
    const insideRoot = (id: number) => rootId != null && (id === rootId || ancestors(id).includes(Number(rootId)));

    const roots: Node[] = [];
    for (const r of rows) {
      if (r.group_id == null) continue;
      const id = Number(r.group_id);
      const node = nodes.get(id)!;
      if (id === rootId) { roots.push(node); continue; }
      if (insideRoot(id)) {
        const parent = r.parent_id != null ? nodes.get(Number(r.parent_id)) : undefined;
        (parent ? parent.children : roots).push(node);
        continue;
      }
      // Outside Sundry Debtors: hang it off the nearest ancestor that itself
      // holds bills, else stand it on its own.
      const host = ancestors(id).map(a => nodes.get(a)).find(n => n && !insideRoot(Number(n.id)) && n.own.bills > 0);
      (host ? host.children : roots).push(node);
    }
    const roll = (n: Node, depth: number): Buckets => {
      n.depth = depth;
      const t = addInto(ZERO(), n.own);
      for (const c of n.children) addInto(t, roll(c, depth + 1));
      n.roll = t;
      n.children.sort((a, b) => b.roll.amount - a.roll.amount);
      return t;
    };
    for (const n of [...roots, ...loose]) roll(n, 0);
    // Sundry Debtors leads — that is what this report is about; the rest of
    // the chart of accounts follows, biggest first.
    return [...roots, ...loose].sort((a, b) =>
      a.name === ROOT_GROUP ? -1 : b.name === ROOT_GROUP ? 1 :
      a.name === UNGROUPED ? 1 : b.name === UNGROUPED ? -1 : b.roll.amount - a.roll.amount);
  }, [rows, scope]);

  // Open Sundry Debtors on arrival, so the report lands where the work is.
  useEffect(() => {
    if (scope !== 'group' || !tree.length) return;
    setExpanded(prev => {
      if (prev.size) return prev;
      const root = tree.find(n => n.name === ROOT_GROUP);
      return root ? new Set([`g${root.id}`]) : prev;
    });
  }, [tree, scope]);

  const toggle = (key: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const lines = useMemo<Line[]>(() => {
    if (scope !== 'group') {
      return rows.map(r => ({ key: r.label, label: r.label, depth: 0, figures: r as Buckets }));
    }
    const out: Line[] = [];
    const walk = (n: Node) => {
      // A group nobody owes anything in says nothing here — and the wrappers
      // above Sundry Debtors are all like that.
      if (n.roll.bills === 0) return;
      const key = `g${n.id}`;
      const hasKids = n.children.length > 0;
      const isOpen = expanded.has(key);
      out.push({ key, label: n.name, depth: n.depth, figures: n.roll, nodeId: n.id, expandable: hasKids, expanded: isOpen });
      if (!hasKids || !isOpen) return;
      // A parent's own parties, listed beside its sub-groups so the children
      // and this line together account for the parent's roll-up.
      if (n.own.bills > 0) {
        out.push({ key: `${key}-direct`, label: `${n.name} — own parties`, depth: n.depth + 1, figures: n.own, nodeId: n.id, direct: true });
      }
      for (const c of n.children) walk(c);
    };
    for (const n of tree) walk(n);
    return out;
  }, [scope, rows, tree, expanded]);

  // Totals come from the raw rows: each bill counts once, at its own group.
  const totals = useMemo(() => rows.reduce((t, r) => addInto(t, r), ZERO() as any), [rows]);

  // Straight to the bills behind a number: the Outstanding report, filtered to
  // that person, group (with its sub-groups) or group's own parties.
  const openBills = (line: Line, bucket: string) => {
    const q = new URLSearchParams({ bucket, date_to: dateTo });
    if (scope !== 'group') q.set('by', line.label);
    else if (line.nodeId == null) q.set('grp', 'none');
    else if (line.direct) q.set('grp', String(line.nodeId));       // this group only
    else q.set('group_id', String(line.nodeId));                   // group + sub-groups
    navigate(`/reports/outstanding/${side === 'payable' ? 'payable' : 'receivable'}?${q.toString()}`);
  };

  const exportExcel = () => {
    // Export what is on screen: the open rows, counts or amounts.
    const data = lines.map(l => {
      const o: any = scope === 'group'
        ? { Group: `${'    '.repeat(l.depth)}${l.label}` }
        : { User: l.label };
      for (const c of COLUMNS) o[c.label] = Number(l.figures[mode === 'count' ? c.key : c.amountKey] || 0);
      return o;
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), mode === 'count' ? 'Bill count' : 'Amount');
    XLSX.writeFile(wb, `followup-by-${scope}-${mode}-${dateTo}.xlsx`);
  };

  const cell = 'px-2 py-1.5 border border-slate-200 text-right tabular-nums';
  const head = 'px-2 py-2 border border-slate-200 text-right font-semibold bg-slate-100 whitespace-nowrap';
  const muted = (l: Line) => l.label === UNFOLLOWED || l.label === UNGROUPED;

  return (
    <div className="flex flex-col h-full bg-white">
      {/* ── Header ── */}
      <div className="flex-none flex items-center justify-between gap-3 px-3 py-2.5 border-b border-slate-200">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => navigate(-1)} className="p-1 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"><ChevronLeft size={18} /></button>
          <h1 className="text-[15px] font-bold text-slate-800 truncate">{scope === 'group' ? 'Followup by Group' : 'Followup by User'}</h1>
          <span className="text-[11px] text-slate-500 whitespace-nowrap">
            {loading ? 'Loading…' : `${scope === 'group' ? `${rows.filter(r => r.bills > 0).length} group(s)` : `${rows.length} user(s)`} · ${totalBills} bills`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-stretch border border-slate-300 rounded-lg overflow-hidden">
            {([['user', 'User', User, '/reports/followup-users'], ['group', 'Group', Layers, '/reports/followup-groups']] as const).map(([sc, label, Icon, path]) => (
              <button
                key={sc}
                onClick={() => navigate(path)}
                className={`flex items-center gap-1 px-2.5 py-1.5 text-[12px] font-bold transition-colors ${scope === sc ? 'bg-slate-700 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                title={sc === 'user' ? 'One row per person following up' : 'Ledger groups, from Sundry Debtors down'}
              >
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>
          <div className="flex items-stretch border border-slate-300 rounded-lg overflow-hidden">
            {([['count', 'Count', Hash], ['amount', 'Amount', IndianRupee]] as const).map(([m, label, Icon]) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`flex items-center gap-1 px-2.5 py-1.5 text-[12px] font-bold transition-colors ${mode === m ? 'bg-blue-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                title={m === 'count' ? 'Show number of bills' : 'Show outstanding amount'}
              >
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>
          <select value={side} onChange={e => setSide(e.target.value as any)}
            className="text-[13px] border border-slate-300 rounded-lg pl-2 pr-6 py-1.5 bg-white outline-none">
            <option value="receivable">Receivable</option>
            <option value="payable">Payable</option>
            <option value="all">Both sides</option>
          </select>
          <div className="flex items-stretch border border-slate-300 rounded-lg bg-white overflow-hidden">
            <span className="flex items-center px-2 bg-slate-100 border-r border-slate-300 text-[10px] font-bold uppercase text-slate-500">As on</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="text-[13px] text-slate-700 outline-none bg-transparent px-2 py-1.5 w-[130px]" />
          </div>
          <button onClick={load} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" title="Refresh">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={exportExcel} disabled={!rows.length} className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 disabled:opacity-40" title="Export to Excel">
            <FileSpreadsheet size={16} />
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="flex-1 min-h-0 overflow-auto">
        {error ? (
          <div className="py-14 text-center text-red-500 text-sm">{error}</div>
        ) : loading ? (
          <div className="py-14 text-center text-slate-400 text-sm"><RefreshCw size={20} className="animate-spin mx-auto mb-2 text-blue-400" /> Loading…</div>
        ) : lines.length === 0 ? (
          <div className="py-14 text-center text-slate-400 text-sm">No outstanding bills for this selection.</div>
        ) : (
          <table className="w-full border-collapse text-[12.5px]">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-100 text-slate-600">
                <th className="px-2 py-2 border border-slate-200 text-left font-semibold bg-slate-100">{scope === 'group' ? 'Group' : 'User'}</th>
                {COLUMNS.map(c => (
                  <th key={c.key} className={head} style={{ color: c.color }}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map(l => (
                <tr key={l.key} className={`hover:bg-blue-50 ${muted(l) || l.direct ? 'bg-slate-50 text-slate-500' : ''}`}>
                  <td className="px-2 py-1.5 border border-slate-200">
                    <div className="flex items-center gap-1" style={{ paddingLeft: `${l.depth * 16}px` }}>
                      {l.expandable ? (
                        <button onClick={() => toggle(l.key)} className="p-0.5 rounded text-slate-400 hover:bg-slate-200 hover:text-slate-700" title={l.expanded ? 'Collapse' : 'Expand sub-groups'}>
                          {l.expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                      ) : <span className="inline-block w-[19px]" />}
                      <span className={l.direct ? 'text-[12px] italic' : 'font-semibold text-slate-800'}>{l.label}</span>
                    </div>
                  </td>
                  {COLUMNS.map(c => {
                    const count = Number(l.figures[c.key] || 0);
                    const money = Number(l.figures[c.amountKey] || 0);
                    return (
                      <td key={c.key} className={cell}>
                        {count > 0 ? (
                          <button
                            onClick={() => openBills(l, c.bucket)}
                            className="font-bold hover:underline"
                            style={{ color: c.color }}
                            title={mode === 'count' ? `${fmt(money)} — show these bills` : `${count} bill(s) — show them`}
                          >
                            {mode === 'count' ? count : fmt(money)}
                          </button>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot className="sticky bottom-0">
              <tr className="bg-slate-100 font-bold text-slate-800">
                <td className="px-2 py-2 border border-slate-200">Total</td>
                {COLUMNS.map(c => {
                  const v = Number(totals[mode === 'count' ? c.key : c.amountKey] || 0);
                  return <td key={c.key} className={cell}>{v ? (mode === 'count' ? v : fmt(v)) : '—'}</td>;
                })}
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
};

export default FollowupUserReport;
